// WebComponent<product-details-nav>
class ProductDetailsNav extends HTMLElement {
  constructor() {
    super();

    this.buttonContainer = this.querySelector('.product-details-nav__items');
    this.buttons = Array.from(this.buttonContainer?.querySelectorAll('.product-details-nav__item') || []);
    this.targets = {};
    this.observers = {};
    this.targetKeyMap = new Map();
    this.activeTab = null;
    this.periodicTimer = null;
    this.periodicRuns = 0;
    this.scrollTicking = false;
  }

  connectedCallback() {
    this.initEvents();
    this.checkModules();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.checkModules();
        this.startPeriodicChecks();
      }, { once: true });
    } else {
      this.checkModules();
      this.startPeriodicChecks();
    }
    document.addEventListener('product-recommendations:loaded', () => {
      this.checkModules();
    });
  }

  initEvents() {
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const tab = btn.getAttribute('data-tab');
        const target = this.targets[tab];
        if (!target) return;
        this.scrollToTarget(target);
      });
    });
    this.onScroll = () => {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.scrollTicking = false;
        this.handleScroll();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  getButton(tab) {
    return this.buttons.find((b) => b.getAttribute('data-tab') === tab);
  }

  parseCssVarPx(varName, fallbackPx) {
    const val = getComputedStyle(document.documentElement).getPropertyValue(varName);
    const num = parseFloat(val);
    return Number.isFinite(num) ? num : fallbackPx;
  }

  checkModules() {
    this.targets = {
      overview: document.querySelector('[data-jump-name="overview"]'),
      reviews: document.getElementById('reviews-section'),
      faq: document.querySelector('[id^="product-faq__"]')
        || document.getElementById('faq-section'),
      recommended: document.querySelector('custom-related-products'),
      'user-manual': document.querySelector('[data-section-type="manual-section"]'),
    };

    Object.entries(this.targets).forEach(([tab, el]) => {
      const btn = this.getButton(tab);
      if (!btn) return;

      let shouldHide = !el;

      if (!shouldHide && tab === 'faq') {
        const faqBlock = el.querySelector('.Avada-FAQs__block');
        let hasContent = false;

        if (faqBlock) {
          if (faqBlock.querySelector('.Avada-FAQs__qa')) {
            hasContent = true;
          } else if (faqBlock.textContent && faqBlock.textContent.trim().length > 0) {
            hasContent = true;
          } else {
            // 无 faq 则不跳转
            this.targets.faq = null;
          }
          // 强制展示
          hasContent = true;
        } else if (el.querySelector('details, .accordion, h2, h3')) {
          // 仓内 product-faq.liquid：用 <details>/accordion/heading 判断有无内容
          hasContent = true;
        }

        if (!hasContent) {
          shouldHide = true;
        }
      }

      if (tab === 'user-manual') {
        btn.classList.remove('hide');
        return;
      }

      if (shouldHide) {
        btn.classList.add('hide');
      } else {
        btn.classList.remove('hide');
      }
    });

    this.setupObservers();
    this.handleScroll();
  }

  setupObservers() {
    // Disconnect previous observers
    Object.values(this.observers).forEach((obs) => {
      try { obs.disconnect(); } catch {}
    });
    this.observers = {};
    this.targetKeyMap = new Map();

    const rootMargin = '-20vh 0px -20vh 0px';
    Object.entries(this.targets).forEach(([tab, el]) => {
      if (!el) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setActiveTab(tab);
          }
        });
      }, { rootMargin, threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
      observer.observe(el);
      this.observers[tab] = observer;
      this.targetKeyMap.set(el, tab);
    });
  }

  setActiveTab(tab) {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.buttons.forEach((b) => b.classList.remove('active'));
    const btn = this.getButton(tab);
    if (btn) btn.classList.add('active');
  }

  handleScroll() {
    const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const midY = currentY + window.innerHeight * 0.5;
    
    // Collect all valid targets with their positions
    const validTargets = [];
    Object.entries(this.targets).forEach(([tab, el]) => {
      if (!el || !el.isConnected) return;
      const rect = el.getBoundingClientRect();
      const top = rect.top + currentY;
      validTargets.push({ tab, top });
    });

    // Sort targets by their top position
    validTargets.sort((a, b) => a.top - b.top);

    // Find the last target whose top is above the mid line
    let newTab = null;
    for (let i = 0; i < validTargets.length; i++) {
      if (midY >= validTargets[i].top) {
        newTab = validTargets[i].tab;
      } else {
        // Since targets are sorted, if current target is below midY, 
        // all subsequent targets will also be below.
        break;
      }
    }

    if (newTab) this.setActiveTab(newTab);
  }

  startPeriodicChecks() {
    if (this.periodicTimer) {
      clearInterval(this.periodicTimer);
    }
    this.periodicRuns = 0;
    this.periodicTimer = setInterval(() => {
      this.periodicRuns += 1;
      this.checkModules();
      if (this.periodicRuns >= 5) {
        clearInterval(this.periodicTimer);
        this.periodicTimer = null;
      }
    }, 2000);
  }

  scrollToTarget(target) {
    const rect = target.getBoundingClientRect();
    const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const targetY = rect.top + currentY;

    const announcementBarHeight = this.parseCssVarPx('--announcement-bar-height', 67);
    const headerHeight = this.parseCssVarPx('--header-height', 53);
    const extraViewportOffset = Math.max(0, Math.floor(window.innerHeight * 0.10));

    const isScrollingDown = targetY > currentY;
    const headerVisible = !document.body.classList.contains('shopify-section-header-hidden');

    let offset = announcementBarHeight;
    if (!isScrollingDown || headerVisible) {
      // Scrolling up OR header currently visible: account for header height
      offset += headerHeight;
    } else {
      // Scrolling down and header hidden: header height not needed
    }
    offset += extraViewportOffset;

    const desiredTop = Math.max(0, targetY - offset);
    window.scrollTo({ top: desiredTop, behavior: 'smooth' });
  }
}
customElements.define('product-details-nav', ProductDetailsNav);
